/**
 * Context que expone los assets subidos en BD (documentos + vídeos)
 * indexados por slug. Se monta a nivel `OnboardingLayout` para que
 * cualquier componente hijo (DocumentCard, VideoEmbed, ChapterPage,
 * etc.) pueda mirar si un slug del catálogo estático ya tiene fichero
 * o URL en producción.
 *
 * Estrategia de fusión:
 *   1. El componente recibe la entrada estática del JSON (título,
 *      descripción, y `url` / `embedUrl` como fallback nullable).
 *   2. Consulta este contexto por slug.
 *   3. Si el contexto tiene entrada, la URL de BD gana sobre el JSON.
 *   4. Si no, se pinta como "Pendiente" (comportamiento original).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { OnboardingAsset } from "@/services/api/onboardingAssetService";
import { onboardingAssetService } from "@/services/api/onboardingAssetService";

interface Ctx {
  assetsBySlug: Map<string, OnboardingAsset>;
  loading: boolean;
  refresh: () => Promise<void>;
}

const OnboardingAssetsContext = createContext<Ctx>({
  assetsBySlug: new Map(),
  loading: true,
  refresh: async () => {},
});

export const OnboardingAssetsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [assetsBySlug, setAssetsBySlug] = useState<Map<string, OnboardingAsset>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await onboardingAssetService.list();
      setAssetsBySlug(new Map(list.map((a) => [a.slug, a])));
    } catch {
      // 401/403/500 → seguimos con el catálogo estático (todo pendiente).
      setAssetsBySlug(new Map());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ assetsBySlug, loading, refresh }),
    [assetsBySlug, loading, refresh],
  );

  return (
    <OnboardingAssetsContext.Provider value={value}>
      {children}
    </OnboardingAssetsContext.Provider>
  );
};

export function useOnboardingAssets(): Ctx {
  return useContext(OnboardingAssetsContext);
}

/** Helper — devuelve la URL efectiva para un slug: BD si existe, si
 *  no el fallback estático. Encapsula la regla de precedencia. */
export function resolveDocumentUrl(
  slug: string,
  fallback: string | null,
  assets: Map<string, OnboardingAsset>,
): string | null {
  const bd = assets.get(slug);
  if (bd?.url) return bd.url;
  return fallback;
}

export function resolveVideoUrl(
  slug: string,
  fallback: string | null,
  assets: Map<string, OnboardingAsset>,
): string | null {
  const bd = assets.get(slug);
  if (bd) {
    // Precedencia: URL firmada del backend (S3 privado) > URL externa
    // (YouTube/Vimeo/CDN público) > fallback estático del JSON.
    if (bd.url) return bd.url;
    if (bd.video_url) return bd.video_url;
  }
  return fallback;
}
